import clsx from 'clsx'
import { Price } from './Price'

import { useBadges } from '@/components/snippets/badge/use-badge'
import { usePromoLines } from '@/components/snippets/promo-line/use-promo-lines'

import { ResultImage } from './ResultImage'
import { ResultsSwatches } from './ResultSwatches'
import { useSwatches } from '../hooks/useSwatches'
import { useBeaconLogClick } from '../hooks/useBeaconAnalytics'
import { Badge } from '@/components/snippets/badge/badge-render'
import { PromoLine } from '@/components/snippets/promo-line/render-promo-line'
import { ResultQuickAdd } from './ResultQuickAdd'
import { KlaviyoStarRating } from './KlaviyoStarRating'
import { scrollHistoryManager } from '@/components/helpers/scroll-history'

export function ResultsCard({ result, className, pageType, query, index, options = {}, imageSizes, imageWidths }) {
  if (!(result.images?.length > 0 || result.image)) return null

  const { currentResult, updateSwatch } = useSwatches(result)
  const criteria = {
    tags: currentResult?.tags ?? [],
    productIds: [currentResult.id],
    collectionIds: currentResult.collection_id
  }
  const badges = useBadges(criteria)
  const promoLines = usePromoLines(criteria)

  // Max 2 flags per card: one of each when both badges and promolines match,
  // otherwise up to 2 of whichever type is present
  const visibleBadges = badges.slice(0, promoLines.length ? 1 : 2)
  const visiblePromoLines = promoLines.slice(0, badges.length ? 1 : 2)

  const shownBadges = options.hideBadges ? [] : visibleBadges
  const shownPromoLines = options.hidePromolines ? [] : visiblePromoLines

  const [variant] = currentResult.variants

  const clickLogger = useBeaconLogClick()

  // Analytics handling
  const handleLogClick = (e) => {
    let collectionState = ''
    if (typeof collection !== 'undefined') {
      collectionState = collection
    }
    scrollHistoryManager.updateHistoryStateWithCollectionInfo(pageType, currentResult, collectionState, query)
    clickLogger(currentResult)
  }

  // This is wrapped in an extra div to support injection from Wishlist KING
  // DONT REMOVE THAT DIV 🔫
  return (
    <div className={clsx('group/card relative flex h-full w-full grow', className)} id={`result__${result.handle}`}>
      <div className="flex flex-col w-full grow">
        <div className="relative overflow-hidden aspect-1 shrink-0">
          <a href={currentResult.url} onClick={handleLogClick} data-dl-track="select_item" data-dl-product-id={currentResult.uid} data-dl-index={index}>
            <ResultImage result={currentResult} {...(imageSizes && { sizes: imageSizes })} {...(imageWidths && { widths: imageWidths })}>
              <div className="flex flex-col px-3 py-3 md:px-6 md:py-6">
                {shownBadges.map((badge) => (
                  <Badge key={badge.text} badge={badge} badgeLocation="PLP" product={currentResult} />
                ))}
                {shownPromoLines.map((promoLine) => (
                  <PromoLine key={promoLine.title} promoline={promoLine} />
                ))}
              </div>
            </ResultImage>
          </a>

          <ResultQuickAdd
            result={currentResult}
            badges={visibleBadges}
            promoLines={visiblePromoLines}
          />
        </div>
        <div className="flex flex-col grow gap-3 p-4 md:px-0 md:py-6">
          <div className="flex flex-col gap-2">
            <p className="font-bold paragraph-sm" dangerouslySetInnerHTML={{ __html: currentResult.brand }} />
            <a className="paragraph-md" href={currentResult.url} data-dl-track="select_item" data-dl-product-id={currentResult.uid} data-dl-index={index} dangerouslySetInnerHTML={{ __html: currentResult.mfield_utopia_customer_facing_product_name }} />
            <KlaviyoStarRating result={currentResult} />
          </div>
          <hr className="mt-auto h-px bg-neutrals-light-grey" />
          <div className="flex justify-between">
            <Price price={variant.price} compareAtPrice={variant?.compare_at_price} />
            {!options.hideSwatches && (
              <ResultsSwatches result={currentResult} swatches={result.swatches} onSelectSwatch={updateSwatch} />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
